import { useState, useMemo, useCallback } from "react";
import { Shield, Building2, Calendar, X } from "lucide-react";
import { Label } from "../../components/ui/label";
import { Button } from "../../components/ui/button";
import { Select } from "antd";
import { useQuery } from "@tanstack/react-query";
import {
  DropdownService,
  SchemaService,
  DynamicModuleService,
  PtsAttendanceService,
} from "@/services/APIs";
import { getEmployeeId } from "@/lib/storeManager";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Card, CardContent } from "../../components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";

function getTodayDateInputValue(): string {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

const EMPLOYEE_SCHEMA_NAME = "PTS Emp";
const ATTENDANCE_SCHEMA_NAME = "PTS Attendance";

const MONTHLY_DIFF_EXCLUDED_KEYS = new Set([
  "emp_id",
  "emp_name",
  "monthly_diff",
  "employee_id",
  "monthly_difference",
  "difference",
]);

function normalizeAttendanceDateColumnKeyToDDMMYYYY(
  key: string
): string | null {
  const k = String(key).trim();

  const hyphen = k.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
  if (hyphen) {
    const d = parseInt(hyphen[1], 10);
    const mo = parseInt(hyphen[2], 10);
    const y = parseInt(hyphen[3], 10);
    if (d >= 1 && d <= 31 && mo >= 1 && mo <= 12 && y >= 1900 && y <= 2100) {
      return `${String(d).padStart(2, "0")}-${String(mo).padStart(2, "0")}-${y}`;
    }
  }

  const slash = k.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (slash) {
    const d = parseInt(slash[1], 10);
    const mo = parseInt(slash[2], 10);
    const y = parseInt(slash[3], 10);
    if (d >= 1 && d <= 31 && mo >= 1 && mo <= 12 && y >= 1900 && y <= 2100) {
      return `${String(d).padStart(2, "0")}-${String(mo).padStart(2, "0")}-${y}`;
    }
  }

  const iso = k.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/);
  if (iso) {
    const y = parseInt(iso[1], 10);
    const mo = parseInt(iso[2], 10);
    const d = parseInt(iso[3], 10);
    if (d >= 1 && d <= 31 && mo >= 1 && mo <= 12 && y >= 1900 && y <= 2100) {
      return `${String(d).padStart(2, "0")}-${String(mo).padStart(2, "0")}-${y}`;
    }
  }

  return null;
}

function isMonthlyDiffDateLikeKey(key: string): boolean {
  if (MONTHLY_DIFF_EXCLUDED_KEYS.has(key)) {
    return false;
  }
  return normalizeAttendanceDateColumnKeyToDDMMYYYY(key) !== null;
}

function compareDDMMYYYYStrings(a: string, b: string): number {
  const pa = a.split("-").map(Number);
  const pb = b.split("-").map(Number);
  if (pa[2] !== pb[2]) {
    return pa[2] - pb[2];
  }
  if (pa[1] !== pb[1]) {
    return pa[1] - pb[1];
  }
  return pa[0] - pb[0];
}

// function csvExcelSafeDateColumnHeader(ddmmyyyy: string): string {
//   const inner = `\t${ddmmyyyy}`;
//   return `"${inner.replace(/"/g, '""')}"`;
// }

const MONTH_OPTIONS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const MyAttendanceReportPage = () => {
  const employeeId = getEmployeeId() ?? "";

  const [activeTab, setActiveTab] = useState<
    "daily" | "monthly" | "attendance"
  >("monthly");

  const [startDate, setStartDate] = useState<string>(getTodayDateInputValue);

  // Attendance Reports tab only — all employees + filter (not locked to login user)
  const [reportStartDate, setReportStartDate] = useState<string>(
    getTodayDateInputValue
  );
  const [reportSelectedEmployee, setReportSelectedEmployee] =
    useState<string>("all");

  const [selectedMonth, setSelectedMonth] = useState<string>(
    MONTH_OPTIONS[new Date().getMonth()]
  );
  const [selectedYear, setSelectedYear] = useState<number>(
    new Date().getFullYear()
  );

  const formatDateForAPI = useCallback(
    (dateString: string, isEndDate: boolean = false) => {
      if (!dateString) {
        return null;
      }
      const [year, month, day] = dateString.split("-").map(Number);
      if (isEndDate) {
        return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T23:59:59`;
      }
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}T00:00:00`;
    },
    []
  );

  const { data: employeeOptions } = useQuery({
    queryKey: ["employee-dropdown", EMPLOYEE_SCHEMA_NAME],
    queryFn: () => DropdownService.getDropdownData(EMPLOYEE_SCHEMA_NAME),
    enabled: !!EMPLOYEE_SCHEMA_NAME,
  });

  // Removed: bulk PTS Emp fetch (page_size=10000) — not needed for self-service report
  // const { data: employeeDataResponse } = useQuery({
  //   queryKey: ["employee-data", EMPLOYEE_SCHEMA_NAME],
  //   queryFn: () =>
  //     DynamicModuleService.getItemsByPage(EMPLOYEE_SCHEMA_NAME, 1, 10000, {}),
  //   enabled: !!EMPLOYEE_SCHEMA_NAME,
  // });

  const { data: columnGridData } = useQuery({
    queryKey: ["columnGridData", ATTENDANCE_SCHEMA_NAME],
    queryFn: () => SchemaService.getColumnGridData(ATTENDANCE_SCHEMA_NAME),
  });

  const employeeOptionsWithNames = useMemo(() => {
    if (!employeeOptions) {
      return [];
    }
    return [...employeeOptions].sort(
      (
        a: { value: string; label: string },
        b: { value: string; label: string }
      ) =>
        (a.label ?? "").localeCompare(b.label ?? "", undefined, {
          sensitivity: "base",
        })
    );
  }, [employeeOptions]);

  const currentEmployeeLabel = useMemo(() => {
    const match = employeeOptionsWithNames.find(
      (e: { value: string; label: string }) =>
        String(e.value) === String(employeeId)
    );
    return match?.label ?? "Current User";
  }, [employeeOptionsWithNames, employeeId]);

  const employeeSelectOptions = useMemo(
    () => [
      { value: "all", label: "All Employees" },
      ...employeeOptionsWithNames.map(
        (employee: { value: string; label: string }) => ({
          value: employee.value,
          label: employee.label,
        })
      ),
    ],
    [employeeOptionsWithNames]
  );

  const getEmployeeNamesById = useCallback(
    (id: string | number | null | undefined) => {
      if (!id) {
        return { firstName: "-", lastName: "" };
      }
      const dropdownEmployee = employeeOptionsWithNames?.find(
        (item: { value: string; label: string }) =>
          String(item.value) === String(id)
      );
      if (dropdownEmployee?.label) {
        const nameParts = dropdownEmployee.label.split(" ");
        return {
          firstName: nameParts[0] ?? "-",
          lastName: nameParts.slice(1).join(" ") ?? "",
        };
      }
      return { firstName: "-", lastName: "" };
    },
    [employeeOptionsWithNames]
  );

  const { data: monthlyDataResponse, isLoading: monthlyLoading } = useQuery({
    queryKey: [
      "my-monthly-attendance-data",
      selectedMonth,
      selectedYear,
      employeeId,
    ],
    queryFn: () =>
      PtsAttendanceService.getMonthlyDifferences(
        selectedMonth,
        selectedYear,
        employeeId || undefined
      ),
    enabled:
      !!selectedMonth &&
      !!selectedYear &&
      !!employeeId &&
      activeTab === "monthly",
  });

  const { data: attendanceDataResponse, isLoading: dailyLoading } = useQuery({
    queryKey: [
      "my-daily-attendance-data",
      ATTENDANCE_SCHEMA_NAME,
      startDate,
      employeeId,
    ],
    queryFn: () => {
      const paramsFilter: Record<string, string> = {};
      if (startDate && startDate.trim() !== "") {
        const formattedStartDate = formatDateForAPI(startDate, false);
        if (formattedStartDate) {
          paramsFilter.date__eq = formattedStartDate;
        }
      }
      if (employeeId) {
        paramsFilter.empname__eq = employeeId;
      }
      paramsFilter.ordering = '["-date", "empname.firstname"]';
      return DynamicModuleService.getItemsByPage(
        ATTENDANCE_SCHEMA_NAME,
        1,
        1000,
        paramsFilter
      );
    },
    enabled: !!ATTENDANCE_SCHEMA_NAME && !!employeeId && activeTab === "daily",
  });

  // Same daily attendance API — all employees + optional employee filter (Attendance Reports tab only)
  const {
    data: reportAttendanceDataResponse,
    isLoading: reportAttendanceLoading,
  } = useQuery({
    queryKey: [
      "my-attendance-reports-data",
      ATTENDANCE_SCHEMA_NAME,
      reportStartDate,
      reportSelectedEmployee,
    ],
    queryFn: () => {
      const paramsFilter: Record<string, string> = {};
      if (reportStartDate && reportStartDate.trim() !== "") {
        const formattedStartDate = formatDateForAPI(reportStartDate, false);
        if (formattedStartDate) {
          paramsFilter.date__eq = formattedStartDate;
        }
      }
      if (reportSelectedEmployee && reportSelectedEmployee !== "all") {
        paramsFilter.empname__eq = reportSelectedEmployee;
      }
      paramsFilter.ordering = '["-date", "empname.firstname"]';
      return DynamicModuleService.getItemsByPage(
        ATTENDANCE_SCHEMA_NAME,
        1,
        1000,
        paramsFilter
      );
    },
    enabled: !!ATTENDANCE_SCHEMA_NAME && activeTab === "attendance",
  });

  const handleClearAttendanceReportFilter = () => {
    const today = getTodayDateInputValue();
    setReportStartDate(today);
    setReportSelectedEmployee("all");
  };

  const monthlyRecords = useMemo(() => {
    if (!monthlyDataResponse) {
      return [];
    }
    if (Array.isArray(monthlyDataResponse)) {
      return monthlyDataResponse;
    }
    if (
      monthlyDataResponse.results &&
      Array.isArray(monthlyDataResponse.results)
    ) {
      return monthlyDataResponse.results;
    }
    if (monthlyDataResponse.data && Array.isArray(monthlyDataResponse.data)) {
      return monthlyDataResponse.data;
    }
    if (monthlyDataResponse.list && Array.isArray(monthlyDataResponse.list)) {
      return monthlyDataResponse.list;
    }
    if (
      monthlyDataResponse.data?.list &&
      Array.isArray(monthlyDataResponse.data.list)
    ) {
      return monthlyDataResponse.data.list;
    }
    return [];
  }, [monthlyDataResponse]);

  const monthlyDiffDateColumnMeta = useMemo(() => {
    const keySet = new Set<string>();
    for (const record of monthlyRecords) {
      if (!record || typeof record !== "object") {
        continue;
      }
      for (const k of Object.keys(record)) {
        if (isMonthlyDiffDateLikeKey(k)) {
          keySet.add(k);
        }
      }
    }
    const entries = [...keySet].map(rawKey => ({
      rawKey,
      display: normalizeAttendanceDateColumnKeyToDDMMYYYY(rawKey) ?? rawKey,
    }));
    entries.sort((a, b) => compareDDMMYYYYStrings(a.display, b.display));
    return entries;
  }, [monthlyRecords]);

  const attendanceData = useMemo(() => {
    if (!attendanceDataResponse) {
      return [];
    }
    if (Array.isArray(attendanceDataResponse)) {
      return attendanceDataResponse;
    }
    if (
      attendanceDataResponse.results &&
      Array.isArray(attendanceDataResponse.results)
    ) {
      return attendanceDataResponse.results;
    }
    return [];
  }, [attendanceDataResponse]);

  const reportAttendanceData = useMemo(() => {
    if (!reportAttendanceDataResponse) {
      return [];
    }
    if (Array.isArray(reportAttendanceDataResponse)) {
      return reportAttendanceDataResponse;
    }
    if (
      reportAttendanceDataResponse.results &&
      Array.isArray(reportAttendanceDataResponse.results)
    ) {
      return reportAttendanceDataResponse.results;
    }
    return [];
  }, [reportAttendanceDataResponse]);

  const findFieldName = useCallback(
    (searchTerms: string[]) => {
      if (!columnGridData?.grid) {
        return null;
      }
      for (const field of columnGridData.grid) {
        const fieldName = field.field_name.toLowerCase();
        const fieldTitle = field.field_title?.toLowerCase() ?? "";
        if (
          searchTerms.some(
            term =>
              fieldName.includes(term.toLowerCase()) ??
              fieldTitle.includes(term.toLowerCase())
          )
        ) {
          return field.field_name;
        }
      }
      return null;
    },
    [columnGridData]
  );

  const dateField = findFieldName(["date", "attendance_date", "work_date"]);
  const inTimeField = findFieldName([
    "in_time",
    "intime",
    "check_in",
    "checkin",
  ]);
  const outTimeField = findFieldName([
    "out_time",
    "outtime",
    "check_out",
    "checkout",
  ]);
  const breakInField = findFieldName([
    "break_in",
    "breakin",
    "break_in_time",
    "breakin1",
  ]);
  const breakOutField = findFieldName([
    "break_out",
    "breakout",
    "break_out_time",
    "breakout1",
  ]);
  const breckhoursField = findFieldName([
    "breckhours",
    "break_hours",
    "breakhours",
  ]);
  const workinghoursField = findFieldName([
    "workinghours",
    "working_hours",
    "productive_hours",
  ]);
  const statusField = findFieldName(["status", "attendance_status", "invalid"]);

  const formatDate = useCallback((dateString: string | null | undefined) => {
    if (!dateString) {
      return "-";
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "-";
      }
      const day = String(date.getDate()).padStart(2, "0");
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const year = date.getFullYear();
      return `${day}-${month}-${year}`;
    } catch {
      return "-";
    }
  }, []);

  const formatTime = useCallback((time: string | null | undefined) => {
    if (!time || time === "-" || time === "null" || time === "undefined") {
      return "-";
    }
    try {
      if (
        typeof time === "string" &&
        time.includes(":") &&
        !time.includes("T")
      ) {
        const [hours, minutes] = time.split(":");
        const hour = parseInt(hours, 10);
        const minute = minutes || "00";
        const period = hour >= 12 ? "PM" : "AM";
        const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
        return `${String(displayHour).padStart(2, "0")}:${minute} ${period}`;
      }
      const d = new Date(time);
      if (isNaN(d.getTime())) {
        return "-";
      }
      return d.toLocaleTimeString("en-US", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
      });
    } catch {
      return "-";
    }
  }, []);

  const getStatusBadgeClass = (status: string): string => {
    switch (status) {
      case "Present":
        return "rounded-full bg-green-100 px-2 py-1 text-xs font-medium text-green-700";
      case "Absent":
        return "rounded-full bg-red-100 px-2 py-1 text-xs font-medium text-red-700";
      case "Invalid":
        return "rounded-full bg-yellow-100 px-2 py-1 text-xs font-medium text-yellow-700";
      default:
        return "rounded-full bg-gray-100 px-2 py-1 text-xs font-medium text-gray-700";
    }
  };

  const formatHoursToHoursMinutes = useCallback(
    (hours: number | string | null | undefined): string => {
      if (hours === null || hours === undefined || hours === "-") {
        return "-";
      }
      if (typeof hours === "string" && hours.includes(":")) {
        const isNegative = hours.startsWith("-");
        const timeStr = isNegative ? hours.substring(1) : hours;
        const timeParts = timeStr.split(":");
        if (timeParts.length === 2) {
          const h = parseInt(timeParts[0], 10) || 0;
          const m = parseInt(timeParts[1], 10) || 0;
          if (h === 0 && m === 0) {
            return "00:00";
          } else if (h === 0) {
            return `${isNegative ? "-" : ""}00:${String(m).padStart(2, "0")}m`;
          } else if (m === 0) {
            return `${isNegative ? "-" : ""}${String(h).padStart(2, "0")}:00h`;
          }
          return `${isNegative ? "-" : ""}${h}h ${m}m`;
        }
      }
      const hoursNum =
        typeof hours === "string"
          ? parseFloat(hours.replace("h", "").trim())
          : typeof hours === "number"
            ? hours
            : 0;
      if (isNaN(hoursNum) || hoursNum === 0) {
        return "-";
      }
      const isNegative = hoursNum < 0;
      const absHours = Math.abs(hoursNum);
      const wholeHours = Math.floor(absHours);
      const minutes = Math.round((absHours - wholeHours) * 60);
      if (minutes === 0) {
        return `${isNegative ? "-" : ""}${wholeHours}h`;
      } else if (wholeHours === 0) {
        return `${isNegative ? "-" : ""}${minutes}m`;
      }
      return `${isNegative ? "-" : ""}${wholeHours}h ${minutes}m`;
    },
    []
  );

  const dailyRecords = useMemo(() => {
    if (attendanceData.length === 0 && employeeId) {
      return [
        {
          empname: employeeId,
          status: "Absent",
          date: startDate,
          isMock: true,
        },
      ];
    }
    return attendanceData;
  }, [attendanceData, employeeId, startDate]);

  const reportAttendanceRecords = useMemo(() => {
    const resolveStatus = (record: any): string => {
      const inTime =
        (inTimeField && record[inTimeField]) ?? record.in_time ?? null;
      const invalid = record.invalid !== undefined ? record.invalid : null;
      const statusValue =
        (statusField && record[statusField]) ?? record.status ?? "";

      if (invalid !== null && typeof invalid === "boolean") {
        return invalid ? "Invalid" : "Present";
      }
      if (statusValue) {
        const statusLower = String(statusValue).toLowerCase();
        if (
          statusLower === "present" ||
          statusLower === "p" ||
          (inTime && inTime !== "-")
        ) {
          return "Present";
        }
        if (
          statusLower === "absent" ||
          statusLower === "a" ||
          statusLower === "invalid"
        ) {
          return statusLower === "invalid" ? "Invalid" : "Absent";
        }
      }
      if (!inTime || inTime === "-") {
        return "Absent";
      }
      return "Present";
    };

    // Specific employee filter — show that employee (Absent if no API row)
    if (reportSelectedEmployee && reportSelectedEmployee !== "all") {
      if (reportAttendanceData.length === 0) {
        return [
          {
            empname: reportSelectedEmployee,
            status: "Absent",
            date: reportStartDate,
            isMock: true,
          },
        ];
      }
      return reportAttendanceData;
    }

    // Default: list ALL employees and overlay fetched attendance response
    return employeeOptionsWithNames.map(
      (employee: { value: string; label: string }) => {
        const empId = String(employee.value);
        const record = reportAttendanceData.find(
          (r: any) =>
            String(
              r?.empname ?? r?.emp_id ?? r?.employee_id ?? r?.employee ?? ""
            ) === empId
        );

        if (record) {
          return {
            ...record,
            empname: empId,
            status: resolveStatus(record),
          };
        }

        return {
          id: `mock-${empId}`,
          empname: empId,
          status: "Absent",
          date: reportStartDate,
          isMock: true,
        };
      }
    );
  }, [
    reportAttendanceData,
    reportSelectedEmployee,
    reportStartDate,
    employeeOptionsWithNames,
    inTimeField,
    statusField,
  ]);

  // const handleExportMonthly = () => {
  //   try {
  //     const csvRows: string[] = [];
  //     csvRows.push("MONTHLY ATTENDANCE DIFFERENCES REPORT");
  //     csvRows.push("");
  //     csvRows.push(`Month,${selectedMonth}`);
  //     csvRows.push(`Year,${selectedYear}`);
  //     csvRows.push(`Employee,${currentEmployeeLabel}`);
  //     csvRows.push("");
  //     csvRows.push("DETAILED MONTHLY DATA");
  //     csvRows.push("");

  //     const escapeCSV = (value: string | null | undefined) => {
  //       if (!value || value === "-") {
  //         return "-";
  //       }
  //       const str = String(value);
  //       if (str.includes(",") || str.includes('"') || str.includes("\n")) {
  //         return `"${str.replace(/"/g, '""')}"`;
  //       }
  //       return str;
  //     };

  //     const headersRow = [
  //       escapeCSV("Employee Name"),
  //       escapeCSV("T.W.H."),
  //       ...monthlyDiffDateColumnMeta.map(({ display }) =>
  //         csvExcelSafeDateColumnHeader(display)
  //       ),
  //     ].join(",");
  //     csvRows.push(headersRow);

  //     const formatMonthlyDiffCell = (monthlyDiff: unknown): string => {
  //       if (monthlyDiff === "-" || monthlyDiff === null || monthlyDiff === "") {
  //         return "-";
  //       }
  //       if (
  //         typeof monthlyDiff === "string" &&
  //         monthlyDiff.includes(":") &&
  //         !/^on-leave$/i.test(monthlyDiff)
  //       ) {
  //         return String(monthlyDiff);
  //       }
  //       return formatHoursToHoursMinutes(monthlyDiff as number | string);
  //     };

  //     monthlyRecords.forEach((record: any) => {
  //       const empId =
  //         record?.emp_id ?? record?.employee_id ?? record?.employee ?? "";
  //       const employeeName =
  //         record?.emp_name ??
  //         (() => {
  //           const names = getEmployeeNamesById(empId);
  //           return `${names.firstName} ${names.lastName}`.trim() || "-";
  //         })();
  //       const monthlyDiff =
  //         record?.monthly_diff ??
  //         record?.monthly_difference ??
  //         record?.difference ??
  //         "-";
  //       const dateCells = monthlyDiffDateColumnMeta.map(({ rawKey }) => {
  //         const val =
  //           (record[rawKey] !== null && String(record[rawKey]).trim()) || "";
  //         return escapeCSV(val || "-");
  //       });
  //       csvRows.push(
  //         [
  //           escapeCSV(employeeName),
  //           escapeCSV(formatMonthlyDiffCell(monthlyDiff)),
  //           ...dateCells,
  //         ].join(",")
  //       );
  //     });

  //     const blob = new Blob([csvRows.join("\n")], {
  //       type: "text/csv;charset=utf-8;",
  //     });
  //     const link = document.createElement("a");
  //     link.href = URL.createObjectURL(blob);
  //     link.download = `my_attendance_report_${selectedMonth}_${selectedYear}.csv`;
  //     link.click();
  //   } catch {
  //     notify.error("Failed to export report");
  //   }
  // };

  if (!employeeId) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center p-4">
        <p className="text-gray-500">
          Employee profile not linked. Please contact your administrator.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-4 md:p-0">
      <Tabs
        value={activeTab}
        onValueChange={v =>
          setActiveTab(v as "daily" | "monthly" | "attendance")
        }
        className="w-full"
      >
        <div className="max-md:scrollbar-thin-grey w-full max-w-full overflow-x-auto rounded-xl bg-gray-100 p-[6px] md:overflow-x-hidden">
          <TabsList className="inline-flex h-auto w-max min-w-full gap-1 md:flex md:w-full">
            <TabsTrigger
              value="monthly"
              className="text-primary flex shrink-0 items-center justify-center gap-2 whitespace-nowrap px-3 data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=inactive]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-md md:w-full md:min-w-0 md:flex-1 md:shrink"
            >
              <Building2 size={16} className="shrink-0" />
              Monthly Report
            </TabsTrigger>
            <TabsTrigger
              value="daily"
              className="text-primary flex shrink-0 items-center justify-center gap-2 whitespace-nowrap px-3 data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=inactive]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-md md:w-full md:min-w-0 md:flex-1 md:shrink"
            >
              <Shield size={16} className="shrink-0" />
              Daily Report
            </TabsTrigger>
            <TabsTrigger
              value="attendance"
              className="text-primary flex shrink-0 items-center justify-center gap-2 whitespace-nowrap px-3 data-[state=active]:border-b-0 data-[state=active]:bg-white data-[state=inactive]:bg-transparent data-[state=active]:text-gray-900 data-[state=active]:shadow-md md:w-full md:min-w-0 md:flex-1 md:shrink"
            >
              <Calendar size={16} className="shrink-0" />
              Attendance Reports
            </TabsTrigger>
          </TabsList>
        </div>
      </Tabs>

      {activeTab === "daily" && (
        <>
          <div className="sticky top-[-1.5rem] z-20 flex flex-col gap-3 rounded-lg p-2 backdrop-blur-md md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
                Daily Attendance Report
              </h1>
              <p className="text-sm text-[#627384]">
                Daily attendance tracking and reporting
              </p>
            </div>
          </div>

          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                <div className="space-y-2">
                  <Label htmlFor="startDate" className="text-sm text-gray-700">
                    Date
                  </Label>
                  <div className="relative">
                    <input
                      type="date"
                      id="startDate"
                      value={startDate}
                      onChange={e => setStartDate(e.target.value || "")}
                      className="w-full rounded-lg border bg-[#fafafa] px-3 py-1 pr-8 text-sm"
                    />
                    {startDate && (
                      <button
                        onClick={() => setStartDate("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear date"
                        type="button"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="employee" className="text-sm text-gray-700">
                    Employee
                  </Label>
                  <Select
                    id="employee"
                    value={employeeId}
                    disabled
                    className="h-[2rem] w-full rounded-xl"
                    options={[
                      { value: employeeId, label: currentEmployeeLabel },
                    ]}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-0">
              <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-primary text-lg font-bold">
                  Daily Attendance Report
                </h2>
                <span className="text-sm text-gray-500">
                  {dailyRecords.length} Record
                  {dailyRecords.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="w-full overflow-x-auto">
                <Table className="min-w-[800px]">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-start font-semibold text-gray-700">
                        Employee Name
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Date
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Check-In
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Break In
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Break Out
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Check-Out
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Break Hours
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Working Hours
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Status
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {dailyLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className="py-8 text-center text-gray-500"
                        >
                          Loading attendance data...
                        </TableCell>
                      </TableRow>
                    ) : dailyRecords.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={9}
                          className="py-8 text-center text-gray-500"
                        >
                          No attendance records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      dailyRecords.map((record: any, index: number) => {
                        const names = getEmployeeNamesById(employeeId);
                        const employeeName =
                          `${names.firstName} ${names.lastName}`.trim();
                        const date =
                          (dateField && record[dateField]) ??
                          record.date ??
                          startDate;
                        const inTime =
                          (inTimeField && record[inTimeField]) ??
                          record.in_time ??
                          null;
                        const outTime =
                          (outTimeField && record[outTimeField]) ??
                          record.out_time ??
                          null;
                        const breakIn =
                          (breakInField && record[breakInField]) ??
                          record.break_in ??
                          null;
                        const breakOut =
                          (breakOutField && record[breakOutField]) ??
                          record.break_out ??
                          null;
                        const breakHours =
                          (breckhoursField && record[breckhoursField]) ??
                          record.breckhours ??
                          null;
                        const workingHours =
                          (workinghoursField && record[workinghoursField]) ??
                          record.workinghours ??
                          null;
                        const invalid =
                          record.invalid !== undefined ? record.invalid : null;
                        const statusValue =
                          (statusField && record[statusField]) ??
                          record.status ??
                          "";
                        let status = "Present";
                        if (invalid !== null && typeof invalid === "boolean") {
                          status = invalid ? "Invalid" : "Present";
                        } else if (statusValue) {
                          const statusLower = String(statusValue).toLowerCase();
                          status =
                            statusLower === "present" ||
                            statusLower === "p" ||
                            (inTime && inTime !== "-")
                              ? "Present"
                              : "Absent";
                        } else if (!inTime || inTime === "-") {
                          status = "Absent";
                        }

                        return (
                          <TableRow
                            key={record.id ?? index}
                            className="hover:bg-gray-50"
                          >
                            <TableCell className="pl-4 text-start font-medium text-gray-900">
                              {employeeName || "-"}
                            </TableCell>
                            <TableCell className="text-center text-gray-900">
                              {formatDate(date)}
                            </TableCell>
                            <TableCell className="text-center text-gray-700">
                              {formatTime(inTime)}
                            </TableCell>
                            <TableCell className="text-center text-gray-700">
                              {formatTime(breakIn)}
                            </TableCell>
                            <TableCell className="text-center text-gray-700">
                              {formatTime(breakOut)}
                            </TableCell>
                            <TableCell className="text-center font-medium text-teal-600">
                              {formatTime(outTime)}
                            </TableCell>
                            <TableCell className="text-center text-orange-600">
                              {breakHours ?? "-"}
                            </TableCell>
                            <TableCell className="text-center font-medium text-blue-600">
                              {workingHours ? `${workingHours}h` : "-"}
                            </TableCell>
                            <TableCell className="text-center">
                              <span className={getStatusBadgeClass(status)}>
                                {status}
                              </span>
                            </TableCell>
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === "attendance" && (
        <>
          <div className="sticky top-[-1.5rem] z-20 flex flex-col gap-3 rounded-lg p-2 backdrop-blur-md md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
                Attendance Reports
              </h1>
              <p className="text-sm text-[#627384]">
                Daily attendance for all employees
              </p>
            </div>
          </div>

          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                <div className="space-y-2">
                  <Label
                    htmlFor="reportStartDate"
                    className="text-sm text-gray-700"
                  >
                    Date
                  </Label>
                  <div className="relative">
                    <input
                      type="date"
                      id="reportStartDate"
                      value={reportStartDate}
                      onChange={e => setReportStartDate(e.target.value || "")}
                      className="w-full rounded-lg border bg-[#fafafa] px-3 py-1 pr-8 text-sm"
                    />
                    {reportStartDate && (
                      <button
                        onClick={() => setReportStartDate("")}
                        className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                        title="Clear date"
                        type="button"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="reportEmployee"
                    className="text-sm text-gray-700"
                  >
                    Employee
                  </Label>
                  <Select
                    id="reportEmployee"
                    value={reportSelectedEmployee}
                    onChange={setReportSelectedEmployee}
                    placeholder="Select employee"
                    className="h-[2rem] w-full rounded-xl"
                    showSearch
                    filterOption={(input, option) => {
                      const label = String(option?.label ?? "");
                      return label.toLowerCase().includes(input.toLowerCase());
                    }}
                    options={employeeSelectOptions}
                  />
                </div>
                <div className="flex items-end">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleClearAttendanceReportFilter}
                    className="w-full"
                  >
                    Clear Filter
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-0">
              <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-primary text-lg font-bold">
                  Attendance Reports
                </h2>
                <span className="text-sm text-gray-500">
                  {reportAttendanceRecords.length} Record
                  {reportAttendanceRecords.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="w-full overflow-x-auto">
                <Table className="min-w-[400px]">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="text-start font-semibold text-gray-700">
                        Employee Name
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Date
                      </TableHead>
                      {/* <TableHead className="text-center font-semibold text-gray-700">
                        Check-In
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Break In
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Break Out
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Check-Out
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Break Hours
                      </TableHead>
                      <TableHead className="text-center font-semibold text-gray-700">
                        Working Hours
                      </TableHead> */}
                      <TableHead className="text-center font-semibold text-gray-700">
                        Status
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reportAttendanceLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="py-8 text-center text-gray-500"
                        >
                          Loading attendance data...
                        </TableCell>
                      </TableRow>
                    ) : reportAttendanceRecords.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3}
                          className="py-8 text-center text-gray-500"
                        >
                          No attendance records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      reportAttendanceRecords.map(
                        (record: any, index: number) => {
                          const recordEmployeeId =
                            record?.empname ??
                            record?.emp_id ??
                            record?.employee_id ??
                            record?.employee ??
                            reportSelectedEmployee;
                          const names = getEmployeeNamesById(recordEmployeeId);
                          const employeeName =
                            `${names.firstName} ${names.lastName}`.trim();
                          const date =
                            (dateField && record[dateField]) ??
                            record.date ??
                            reportStartDate;
                          // const inTime =
                          //   (inTimeField && record[inTimeField]) ??
                          //   record.in_time ??
                          //   null;
                          // const outTime =
                          //   (outTimeField && record[outTimeField]) ??
                          //   record.out_time ??
                          //   null;
                          // const breakIn =
                          //   (breakInField && record[breakInField]) ??
                          //   record.break_in ??
                          //   null;
                          // const breakOut =
                          //   (breakOutField && record[breakOutField]) ??
                          //   record.break_out ??
                          //   null;
                          // const breakHours =
                          //   (breckhoursField && record[breckhoursField]) ??
                          //   record.breckhours ??
                          //   null;
                          // const workingHours =
                          //   (workinghoursField && record[workinghoursField]) ??
                          //   record.workinghours ??
                          //   null;
                          const invalid =
                            record.invalid !== undefined
                              ? record.invalid
                              : null;
                          const statusValue =
                            (statusField && record[statusField]) ??
                            record.status ??
                            "";
                          const inTime =
                            (inTimeField && record[inTimeField]) ??
                            record.in_time ??
                            null;
                          let status = "Present";
                          if (
                            invalid !== null &&
                            typeof invalid === "boolean"
                          ) {
                            status = invalid ? "Invalid" : "Present";
                          } else if (statusValue) {
                            const statusLower =
                              String(statusValue).toLowerCase();
                            status =
                              statusLower === "present" ||
                              statusLower === "p" ||
                              (inTime && inTime !== "-")
                                ? "Present"
                                : statusLower === "invalid"
                                  ? "Invalid"
                                  : "Absent";
                          } else if (!inTime || inTime === "-") {
                            status = "Absent";
                          }

                          return (
                            <TableRow
                              key={record.id ?? index}
                              className="hover:bg-gray-50"
                            >
                              <TableCell className="pl-4 text-start font-medium text-gray-900">
                                {employeeName || "-"}
                              </TableCell>
                              <TableCell className="text-center text-gray-900">
                                {formatDate(date)}
                              </TableCell>
                              {/* <TableCell className="text-center text-gray-700">
                                {formatTime(inTime)}
                              </TableCell>
                              <TableCell className="text-center text-gray-700">
                                {formatTime(breakIn)}
                              </TableCell>
                              <TableCell className="text-center text-gray-700">
                                {formatTime(breakOut)}
                              </TableCell>
                              <TableCell className="text-center font-medium text-teal-600">
                                {formatTime(outTime)}
                              </TableCell>
                              <TableCell className="text-center text-orange-600">
                                {breakHours ?? "-"}
                              </TableCell>
                              <TableCell className="text-center font-medium text-blue-600">
                                {workingHours ? `${workingHours}h` : "-"}
                              </TableCell> */}
                              <TableCell className="text-center">
                                <span className={getStatusBadgeClass(status)}>
                                  {status}
                                </span>
                              </TableCell>
                            </TableRow>
                          );
                        }
                      )
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}

      {activeTab === "monthly" && (
        <>
          <div className="sticky top-[-1.5rem] z-20 rounded-lg p-2 backdrop-blur-md">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div>
                <h1 className="text-xl font-bold text-[#111827] md:text-2xl">
                  Monthly Attendance Report
                </h1>
                <p className="text-sm text-[#627384]">
                  Monthly attendance differences tracking
                </p>
              </div>
              {/* <Button
                onClick={handleExportMonthly}
                className="bg-primary hover:bg-primary/90 flex items-center gap-2 text-white"
              >
                <Download className="h-4 w-4" />
                Export Report
              </Button> */}
            </div>
          </div>

          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-5">
                <div className="space-y-2">
                  <Label htmlFor="month" className="text-sm text-gray-700">
                    Month
                  </Label>
                  <Select
                    id="month"
                    value={selectedMonth}
                    onChange={setSelectedMonth}
                    placeholder="Select month"
                    className="h-[2rem] w-full rounded-xl"
                    options={MONTH_OPTIONS.map(m => ({ value: m, label: m }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="year" className="text-sm text-gray-700">
                    Year
                  </Label>
                  <input
                    type="number"
                    id="year"
                    value={selectedYear}
                    onChange={e =>
                      setSelectedYear(
                        Number(e.target.value) || new Date().getFullYear()
                      )
                    }
                    min="2000"
                    max="2100"
                    className="h-[2rem] w-full rounded-lg border bg-[#fafafa] px-3 text-sm"
                  />
                </div>
                <div className="space-y-2">
                  <Label
                    htmlFor="monthly-employee"
                    className="text-sm text-gray-700"
                  >
                    Employee
                  </Label>
                  <Select
                    id="monthly-employee"
                    value={employeeId}
                    disabled
                    className="h-[2rem] w-full rounded-xl"
                    options={[
                      { value: employeeId, label: currentEmployeeLabel },
                    ]}
                  />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border border-gray-200 bg-white">
            <CardContent className="p-0">
              <div className="flex flex-col gap-4 border-b border-gray-200 p-4 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="text-primary text-lg font-bold">
                  Monthly Differences
                </h2>
                <span className="text-sm text-gray-500">
                  {monthlyRecords.length} Record
                  {monthlyRecords.length !== 1 ? "s" : ""}
                </span>
              </div>
              <div className="scrollbar-thin-grey pts-monthly-diff-scroll w-full overflow-x-auto">
                <Table className="min-w-[800px] border-separate border-spacing-0">
                  <TableHeader>
                    <TableRow className="bg-gray-50">
                      <TableHead className="pts-monthly-diff-sticky-index pts-monthly-diff-sticky-index-header border-b border-gray-200 text-center text-xs">
                        #
                      </TableHead>
                      <TableHead className="pts-monthly-diff-sticky-name pts-monthly-diff-sticky-name-header border-b border-gray-200 text-start text-xs">
                        Employee Name
                      </TableHead>
                      <TableHead className="pts-monthly-diff-sticky-twh pts-monthly-diff-sticky-twh-header border-b border-gray-200 text-center text-xs">
                        T.W.H.
                      </TableHead>
                      {monthlyDiffDateColumnMeta.map(({ rawKey, display }) => (
                        <TableHead
                          key={rawKey}
                          className="min-w-[90px] border-b border-r border-gray-200 text-center text-xs font-semibold text-gray-700"
                        >
                          {display}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {monthlyLoading ? (
                      <TableRow>
                        <TableCell
                          colSpan={3 + monthlyDiffDateColumnMeta.length}
                          className="border-b border-gray-200 py-8 text-center text-gray-500"
                        >
                          Loading monthly attendance data...
                        </TableCell>
                      </TableRow>
                    ) : monthlyRecords.length === 0 ? (
                      <TableRow>
                        <TableCell
                          colSpan={3 + monthlyDiffDateColumnMeta.length}
                          className="border-b border-gray-200 py-8 text-center text-gray-500"
                        >
                          No monthly attendance records found
                        </TableCell>
                      </TableRow>
                    ) : (
                      monthlyRecords.map((record: any, index: number) => {
                        const empId =
                          record?.emp_id ??
                          record?.employee_id ??
                          record?.employee ??
                          "";
                        const employeeName =
                          record?.emp_name ??
                          (() => {
                            const names = getEmployeeNamesById(empId);
                            return (
                              `${names.firstName} ${names.lastName}`.trim() ||
                              "-"
                            );
                          })();
                        const monthlyDiff =
                          record?.monthly_diff ??
                          record?.monthly_difference ??
                          record?.difference ??
                          "-";
                        const isNeg =
                          typeof monthlyDiff === "string" &&
                          monthlyDiff.startsWith("-");
                        const isZero =
                          monthlyDiff === "0" ||
                          monthlyDiff === "0:00" ||
                          monthlyDiff === "00:00" ||
                          monthlyDiff === "+00:00" ||
                          monthlyDiff === "-00:00";

                        return (
                          <TableRow
                            key={empId ?? index}
                            className="group hover:bg-gray-50/80"
                          >
                            <TableCell className="pts-monthly-diff-sticky-index pts-monthly-diff-sticky-index-body border-b border-gray-200 text-center text-sm text-gray-600">
                              {index + 1}
                            </TableCell>
                            <TableCell className="pts-monthly-diff-sticky-name pts-monthly-diff-sticky-name-body border-b border-gray-200 font-medium text-gray-900">
                              {employeeName ?? "-"}
                            </TableCell>
                            <TableCell className="pts-monthly-diff-sticky-twh pts-monthly-diff-sticky-twh-body border-b border-gray-200 text-center font-medium">
                              {monthlyDiff !== "-" && monthlyDiff !== null ? (
                                <span
                                  className={
                                    isZero
                                      ? "text-gray-900"
                                      : isNeg
                                        ? "text-red-600"
                                        : "text-green-600"
                                  }
                                >
                                  {typeof monthlyDiff === "string" &&
                                  monthlyDiff.includes(":")
                                    ? monthlyDiff
                                    : formatHoursToHoursMinutes(monthlyDiff)}
                                </span>
                              ) : (
                                "-"
                              )}
                            </TableCell>
                            {monthlyDiffDateColumnMeta.map(({ rawKey }) => {
                              const val =
                                (record[rawKey] !== null &&
                                  String(record[rawKey]).trim()) ||
                                "";
                              const isOnLeave = /on-leave|on leave/i.test(val);
                              const isAbsent =
                                val === "A" || /^absent$/i.test(val);
                              const isWeekend =
                                val === "Saturday" || val === "Sunday";
                              const cellClass = isOnLeave
                                ? "text-amber-600 font-medium"
                                : isAbsent
                                  ? "text-red-600 font-medium"
                                  : isWeekend
                                    ? "text-gray-500"
                                    : "text-gray-900";
                              return (
                                <TableCell
                                  key={rawKey}
                                  className={`min-w-[90px] border-b border-r border-gray-200 text-center text-sm ${cellClass}`}
                                >
                                  {val || "-"}
                                </TableCell>
                              );
                            })}
                          </TableRow>
                        );
                      })
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
};

export default MyAttendanceReportPage;
